
import React, { useState, useEffect } from 'react';
import { X, Zap, Lock, Mic, Sparkles, Columns, ChevronRight, AlertCircle, Check } from 'lucide-react';
import { API_BASE } from '../apiConfig';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: (plan: 'monthly' | 'yearly') => Promise<void>;
  feature?: string;
}

const FEATURES = [
  { icon: Columns, label: 'Delegate & Postpone Quadrants', desc: 'Full Eisenhower Matrix with all 4 quadrants' },
  { icon: Mic, label: 'AI Voice-to-Task', desc: 'Speak your tasks and let AI structure them' },
  { icon: Sparkles, label: 'AI Task Decomposition', desc: 'Break complex objectives into actionable steps' },
];

const fmt = (cents: number) => {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
};

export const UpgradeModal: React.FC<Props> = ({ isOpen, onClose, onUpgrade, feature }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<'monthly' | 'yearly'>('yearly');
  const [pricing, setPricing] = useState({ monthlyPriceCents: 600, yearlyPriceCents: 5900 });

  useEffect(() => {
    if (!isOpen) return;
    fetch(`${API_BASE}/api/config/pricing`)
      .then(r => r.json())
      .then(data => {
        if (data.monthlyPriceCents && data.yearlyPriceCents) setPricing(data);
      })
      .catch(() => {}); // Fallback to defaults silently
  }, [isOpen]);

  if (!isOpen) return null;

  const monthly = pricing.monthlyPriceCents;
  const yearly = pricing.yearlyPriceCents;
  const monthlyEquiv = yearly / 12;
  const savingsPercent = Math.round((1 - monthlyEquiv / monthly) * 100);

  const handleUpgrade = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await onUpgrade(plan);
      setTimeout(() => setIsLoading(false), 3000);
    } catch (e: any) {
      setError(e?.message || 'Could not connect to payment provider. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-[2rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100" onClick={e => e.stopPropagation()}>

        {/* Gradient header */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 px-8 pt-8 pb-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 blur-[60px] rounded-full pointer-events-none" />
          <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/20 transition-colors text-white/70">
            <X size={18} />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-white/20 rounded-xl">
              <Zap size={22} fill="currentColor" />
            </div>
            <h2 className="text-xl font-black tracking-tight">Unlock Full Power</h2>
          </div>
          {feature && (
            <p className="text-sm text-blue-100 font-medium">
              <Lock size={12} className="inline mr-1.5 -mt-0.5" />
              {feature} requires Premium
            </p>
          )}
        </div>

        {/* Features */}
        <div className="px-8 py-6 space-y-4">
          {FEATURES.map((f, i) => (
            <div key={i} className="flex items-start gap-3.5">
              <div className="p-2 bg-blue-50 text-blue-600 rounded-lg shrink-0 mt-0.5">
                <f.icon size={16} />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">{f.label}</p>
                <p className="text-xs text-slate-500">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Plan toggle + CTA */}
        <div className="px-8 pb-8 space-y-5">
          {/* Plan selector */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setPlan('monthly')}
              className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                plan === 'monthly'
                  ? 'border-blue-600 bg-blue-50/50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              {plan === 'monthly' && <Check size={14} className="absolute top-3 right-3 text-blue-600" />}
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Monthly</p>
              <p className="text-2xl font-black text-slate-900 mt-1">{fmt(monthly)}</p>
              <p className="text-[11px] text-slate-500">/month</p>
            </button>
            <button
              type="button"
              onClick={() => setPlan('yearly')}
              className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                plan === 'yearly'
                  ? 'border-blue-600 bg-blue-50/50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              {plan === 'yearly' && <Check size={14} className="absolute top-3 right-3 text-blue-600" />}
              {savingsPercent > 0 && (
                <div className="absolute -top-2.5 right-3 px-2 py-0.5 bg-emerald-500 text-white text-[9px] font-black uppercase rounded-full tracking-wide">Save {savingsPercent}%</div>
              )}
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Yearly</p>
              <p className="text-2xl font-black text-slate-900 mt-1">{fmt(yearly)}</p>
              <p className="text-[11px] text-slate-500">/year <span className="text-emerald-600 font-bold">({fmt(Math.round(monthlyEquiv))}/mo)</span></p>
            </button>
          </div>

          <p className="text-[11px] text-slate-400 text-center">Cancel anytime. No commitments.</p>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            onClick={handleUpgrade}
            disabled={isLoading}
            className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-xl transition-all shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Zap size={16} fill="currentColor" />
                {plan === 'yearly' ? `Upgrade — ${fmt(yearly)}/year` : `Upgrade — ${fmt(monthly)}/month`}
                <ChevronRight size={16} />
              </>
            )}
          </button>

          <button onClick={onClose} className="w-full py-2 text-xs font-medium text-slate-400 hover:text-slate-600 transition-colors">
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};
